
import type { PropType } from 'vue'
import type { NvCalendarValue, NvCalendarValueModes, NvCalendarPeriod, NvCalendarReservedPeriod } from './Calendar.types'

import { computed, defineComponent, onBeforeMount, reactive, ref } from 'vue'

export default defineComponent({
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        yearShowed: {
            type: Boolean,
            default: true,
        },
        viewYear: {
            type: Number,
            default: () => (new Date().getFullYear())
        },
        viewMonth: {
            type: Number,
            default: () => (new Date().getMonth())
        },
        valueMode: {
            type: String as PropType<NvCalendarValueModes>,
            default: 'single-date'
        },
        value: {
            type: [null, Date, Array] as PropType<NvCalendarValue>,
            default: null
        },
        reservedPeriods: {
            type: Array as PropType<Array<NvCalendarReservedPeriod>>,
            default: () => []
        },
        periodSelectionStream: {
            type: [null, Array] as PropType<NvCalendarPeriod>,
            default: null
        },
        calendarId: {
            type: Number,
            default: () => Number(String(Date.now()) + String(Math.floor(Math.random() * 1000)))
        }
    },
    emits: ['update:value', 'change', 'date-click', 'date-mouseover'],
    setup(props, { emit }) {
        const calendarEl = ref<HTMLElement | null>(null)

        const view_state = reactive({
            year: props.viewYear,
            month: props.viewMonth,
            dates: [] as Array<Date>
        })

        const getDates = () => {
            const year = view_state.year
            const month = view_state.month

            const dates: Date[] = []

            const firstDay = new Date(year, month, 1, 12)
            let firstWeekday = firstDay.getDay()
            if (firstWeekday === 0) firstWeekday = 7

            const prevDaysCount = firstWeekday - 1
            if (prevDaysCount > 0) {
                const prevMonthLastDate = new Date(year, month, 0, 12).getDate()
                for (let i = prevMonthLastDate - prevDaysCount + 1; i <= prevMonthLastDate; i++) {
                    dates.push(new Date(year, month - 1, i, 12))
                }
            }

            const daysInMonth = new Date(year, month + 1, 0, 12).getDate()
            for (let i = 1; i <= daysInMonth; i++) {
                dates.push(new Date(year, month, i, 12))
            }

            const totalCells = 35
            const nextDays = totalCells - dates.length

            for (let i = 1; i <= nextDays; i++) {
                dates.push(new Date(year, month + 1, i, 12))
            }

            view_state.dates = dates
        }

        const initializeView = () => {
            getDates()
        }

        onBeforeMount(initializeView)

        const onDateClick = (date: Date) => {
            if (isDateDisabled(date) || isDateInReservedPeriod(date)) return
            emit('date-click', date)

            if (props.valueMode === 'single-date') {
                return
            }

            if (props.valueMode === 'period') {
                if (props.value && Array.isArray(props.value)) {
                    if (props.value.length === 1) {
                        if (props.reservedPeriods.length > 0) {
                            if (
                                props.reservedPeriods.some((reservedPeriod) => {
                                    const firstSelectedDate = (props.value as NvCalendarPeriod)[0].getTime()
                                    const lastSelectedDate = date.getTime()

                                    const firstReservedPeriodDate = reservedPeriod.dates[0].getTime()
                                    const lastReservedPeriodDate = reservedPeriod.dates[1].getTime()

                                    const conditionOne = firstSelectedDate < firstReservedPeriodDate && lastSelectedDate > lastReservedPeriodDate
                                    const conditionTwo = lastSelectedDate < firstReservedPeriodDate && firstSelectedDate > lastReservedPeriodDate

                                    return conditionOne || conditionTwo
                                })
                            ) {
                                return
                            }
                        }


                        if (isDateEarlierThen(props.value[0], date)) {
                            emit('update:value', [props.value[0], date])
                        } else {
                            emit('update:value', [date, props.value[0]])
                        }
                    } else {
                        emit('update:value', [date])
                    }
                }
            }
        }

        const hideAllTooltips = () => {
            if (props.reservedPeriods.length > 0) {
                props.reservedPeriods.forEach((reservedPeriod) => {
                    const tooltip = document.getElementById(`nv-calendar-tooltip-${props.calendarId}-${reservedPeriod.id}`)

                    if (tooltip) {
                        tooltip.style.setProperty('display', 'none')
                    }
                })
            }
        }

        const onDateMouseover = (event: MouseEvent, date: Date) => {
            if (isDateDisabled(date)) return
            emit('date-mouseover', date)

            if (props.reservedPeriods.length > 0) {
                const reservedPeriod = props.reservedPeriods.find((reservedPeriod) => {
                    const firstReservedPeriodDate = reservedPeriod.dates[0].getTime()
                    const lastReservedPeriodDate = reservedPeriod.dates[1].getTime()
                    const hoveredDate = date.getTime()

                    return hoveredDate >= firstReservedPeriodDate && hoveredDate <= lastReservedPeriodDate
                })

                if (reservedPeriod) {
                    const calendarElement = calendarEl.value as HTMLElement
                    const calendarRect = calendarElement.getBoundingClientRect()
                    const tooltip = document.getElementById(`nv-calendar-tooltip-${props.calendarId}-${reservedPeriod.id}`)
                    
                    if (tooltip) {
                        tooltip.style.setProperty('display', 'block')
                        tooltip.style.setProperty('top', `${event.clientY - calendarRect.top + 5}px`)
                        tooltip.style.setProperty('left', `${event.clientX - calendarRect.left + 5}px`)
                    }
                } else {
                    hideAllTooltips()
                }
            }
        }

        const isDateEarlierThen = (d1: Date, d2: Date) => d1.getTime() < d2.getTime()

        const isSameCalendarDate = (a: Date, b: Date) => {
            return (
                a.getFullYear() === b.getFullYear() &&
                a.getMonth() === b.getMonth() &&
                a.getDate() === b.getDate()
            )
        }

        // selected period
        const isDateFirstInPeriod = (date: Date) => {
            if (props.value && Array.isArray(props.value)) {
                if (props.value.length >= 1) {
                    return isSameCalendarDate(props.value[0], date)
                }
            }

            return false
        }

        const isDateLastInPeriod = (date: Date) => {
            if (props.value && Array.isArray(props.value)) {
                if (props.value.length === 2) {
                    return isSameCalendarDate(props.value[1], date)
                }

                if (props.value.length === 1) {
                    return false
                }
            }

            return false
        }

        const isDateInPeriod = (date: Date) => {
            if (props.valueMode === 'period') {
                if (props.value && Array.isArray(props.value)) {
                    if (props.value.length === 2) {
                        const start = props.value[0]
                        const end = props.value[1]

                        const t = date.getTime()
                        return t >= start.getTime() && t <= end.getTime()
                    }

                    if (props.value.length === 1) {
                        const start = props.value[0]
                        const t = date.getTime()
                        return t === start.getTime()
                    }
                }
            }

            return false
        }

        // reserved periods
        const isDateFirstInReservedPeriod = (date: Date) => {
            if (props.reservedPeriods.length) {
                return props.reservedPeriods.some((reservedPeriod) => isSameCalendarDate(reservedPeriod.dates[0], date))
            }

            return false
        }

        const isDateLastInReservedPeriod = (date: Date) => {
            if (props.reservedPeriods.length) {
                return props.reservedPeriods.some((reservedPeriod) => isSameCalendarDate(reservedPeriod.dates[1], date))
            }

            return false
        }

        const isDateInReservedPeriod = (date: Date) => {
            if (props.reservedPeriods.length) {
                return props.reservedPeriods.some((reservedPeriod) => {
                    const start = reservedPeriod.dates[0]
                    const end = reservedPeriod.dates[1]

                    const t = date.getTime()
                    return t >= start.getTime() && t <= end.getTime()
                })
            }

            return false
        }

        const isDateDisabled = (date: Date) => {
            if (props.disabled) return true
            return false
        }

        const isDateFromPreviousMonthOrYear = (date: Date) => {
            if (date.getFullYear() < view_state.year) {
                return true
            }

            if (date.getMonth() < view_state.month) {
                return true
            }

            return false
        }

        const isDateFromNextMonthOrYear = (date: Date) => {
            if (date.getFullYear() > view_state.year) {
                return true
            }

            if (date.getMonth() > view_state.month) {
                return true
            }

            return false
        }

        // computeds
        const viewDaysInMonth = computed(() => new Date(view_state.year, view_state.month + 1, 0, 12).getDate())
        const viewMonthName = computed(() => new Date(view_state.year, view_state.month, 1, 12).toLocaleString('ru-RU', { month: 'long' }))

        return {
            calendarEl,
            view_state,
            onDateClick,
            onDateMouseover,
            hideAllTooltips,
            isDateFirstInPeriod,
            isDateLastInPeriod,
            isDateInPeriod,
            isDateFirstInReservedPeriod,
            isDateLastInReservedPeriod,
            isDateInReservedPeriod,
            isDateDisabled,
            isDateFromPreviousMonthOrYear,
            isDateFromNextMonthOrYear,
            viewDaysInMonth,
            viewMonthName
        }
    }
})
